/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import React, { useState, ReactElement, useContext, useEffect } from "react";
import Loader from "react-loader-spinner";

import { PDFViewer } from "~/components";
import { pdfs } from "~/constants";
import { WindowMeasureContext } from "~/contexts";
import { analytics } from "~/utils";
import { hasMultipleKeys } from "~/utils/helpers";

import ButtonsOverlay from "./ButtonsOverlay";

const ZOOM_STEP = 0.1;

const MIN_ZOOM = 0.5;

const DIVIDER_HEIGHT = 10;
export interface Props {
  pdfName: string;
}

export default function PDFPage({ pdfName }: Props): ReactElement {
  const windowMeasure = useContext(WindowMeasureContext);

  const [, setSeenPages] = useState<Record<number, boolean>>({});
  const [renderedPages, setRenderedPages] = useState({});
  const [currentVisiblePage, setCurrentVisiblePage] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [startTime] = useState(Date.now());
  const [minZoomLevel, setMinZoomLevel] = useState(MIN_ZOOM);
  const [pageDimensions, setPageDimensions] = useState({
    width: 0,
    height: 0,
    isAbleToZoomIn: false,
    isAbleToZoomOut: false,
  });

  const pdf = pdfs[pdfName];

  useEffect(() => {
    let calculatedZoomLevel = windowMeasure.width / pdf.pdfWidth;
    if (calculatedZoomLevel > 1) {
      calculatedZoomLevel = 1;
    }
    setZoomLevel(calculatedZoomLevel);
    if (calculatedZoomLevel !== 0 && calculatedZoomLevel < MIN_ZOOM) {
      setMinZoomLevel(calculatedZoomLevel);
    }
  }, [windowMeasure, pdf]);

  useEffect(() => {
    const width = pdf.pdfWidth * zoomLevel;
    const height = pdf.pdfHeight * zoomLevel;
    setPageDimensions({
      width,
      height,
      isAbleToZoomOut: zoomLevel > minZoomLevel,
      isAbleToZoomIn: width * (zoomLevel + ZOOM_STEP) <= pdf.pdfWidth,
    });
  }, [pdf, zoomLevel, minZoomLevel]);

  let showLoader = true;
  if (pageDimensions.height) {
    const requiredPagesCount = Math.ceil(
      windowMeasure.height / pageDimensions.height
    );

    const requiredPageNumbers = Array.from(
      { length: requiredPagesCount },
      (_value, i) => i + 1
    );

    const hasRenderedRequiredPages =
      requiredPagesCount && hasMultipleKeys(requiredPageNumbers, renderedPages);

    showLoader = !hasRenderedRequiredPages;
  }

  function onVisiblityChange(pageNum: number, isVisible: boolean): void {
    if (!isVisible) return;
    setCurrentVisiblePage(pageNum);
    setSeenPages((prevState) => {
      if (!Object.keys(renderedPages).includes(`${pageNum}`)) return prevState;

      console.log("onVisiblityChange", pageNum);
      analytics.pageView(pageNum);

      return { ...prevState, [pageNum]: true };
    });
  }

  function onPageRenderSuccess(pageNum: number): void {
    if (Object.keys(renderedPages).includes(`${pageNum}`)) return;
    const time = Date.now() - startTime;
    console.log("onPageRenderSuccess", pageNum, time);
    analytics.pageRender(pageNum, time);
    if (currentVisiblePage === pageNum) {
      onVisiblityChange(pageNum, true);
    }
    setRenderedPages((prevState) => ({
      [pageNum]: true,
      ...prevState,
    }));
  }

  function onLoadSuccess(): void {
    console.log("onLoadSuccess", Date.now() - startTime);
  }

  function onZoomInClick(): void {
    setZoomLevel((prevZoom) => prevZoom + ZOOM_STEP);
  }

  function onZoomOutClick(): void {
    setZoomLevel((prevZoom) => {
      let newZoom = prevZoom - ZOOM_STEP;
      if (newZoom < minZoomLevel) {
        newZoom = minZoomLevel;
      }
      return newZoom;
    });
  }

  return (
    <>
      <PDFViewer
        filename={pdf.filename}
        hidePages={windowMeasure.isResizing}
        pageBackgroundColor={pdf.pageBackground}
        pageDividerHeight={DIVIDER_HEIGHT}
        pageHeight={pageDimensions.height}
        pageLoader={
          <Loader
            color={pdf.loaderColor}
            height={100}
            type="Oval"
            width={100}
          />
        }
        pageWidth={pageDimensions.width}
        showLoader={showLoader}
        onLoadSuccess={onLoadSuccess}
        onPageRenderSuccess={onPageRenderSuccess}
        onVisiblityChange={onVisiblityChange}
      />

      <ButtonsOverlay
        showZoomIn={pageDimensions.isAbleToZoomIn}
        showZoomOut={pageDimensions.isAbleToZoomOut}
        onZoomInClick={onZoomInClick}
        onZoomOutClick={onZoomOutClick}
      />
    </>
  );
}
